import { useNavigate } from 'react-router-dom';
import sabotagelogo from '@/assets/sabotage-logo.png';
import { Button } from '@/components/ui/button';
import { DownloadLinks } from '@/components/DownloadLinks';

const LEGAL_LINKS = {
  terms: 'https://app.termly.io/policy-viewer/policy.html?policyUUID=0eef0aaa-538d-4434-9471-2d372eb58c22',
  privacy: 'https://app.termly.io/policy-viewer/policy.html?policyUUID=74b4f847-6b13-45b6-9a95-fa2cd405c4a6',
  cookies: 'https://app.termly.io/policy-viewer/policy.html?policyUUID=b68d815d-0463-455f-8bb1-1aef0ad6efd2',
};

export default function MarketingLayout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background text-foreground font-sans">
      {/* Nav */}
      <nav
        className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-xl px-4 md:px-6 flex items-center justify-between"
        style={{ paddingTop: 'env(safe-area-inset-top)', minHeight: 'calc(56px + env(safe-area-inset-top))' }}
      >
        <img
          src={sabotagelogo}
          alt="Sabotage Fantasy"
          className="h-8 cursor-pointer"
          onClick={() => navigate('/')}
        />
        <div className="flex items-center gap-2">
          <Button onClick={() => navigate('/nfldemo')} size="sm" variant="outline" className="border-orange-500/30 text-orange-400 hover:bg-orange-500/10 hover:text-orange-300">
            Try Demo
          </Button>
          <Button onClick={() => navigate('/auth')} size="sm">
            Sign In
          </Button>
        </div>
      </nav>

      {/* Page content */}
      <div className="container mx-auto px-4 py-8 max-w-3xl space-y-10 pb-12">
        {children}
      </div>

      {/* Footer */}
      <footer className="border-t border-border bg-background text-center space-y-4 py-8 px-4">
        <DownloadLinks />
        <img src={sabotagelogo} alt="Sabotage Fantasy" className="h-7 mx-auto opacity-60" />
        <div className="flex gap-5 justify-center flex-wrap">
          {[
            { label: 'Terms & Conditions', href: LEGAL_LINKS.terms },
            { label: 'Privacy Policy', href: LEGAL_LINKS.privacy },
            { label: 'Cookie Policy', href: LEGAL_LINKS.cookies },
            { label: 'Responsible Gaming', href: '/responsible-gaming' },
            { label: 'How Withdrawals Work', href: '/withdrawals' },
            { label: 'How to Play', href: '/how-to-play' },
            { label: 'Contest Rules', href: '/contest-rules' },
          ].map(l => (
            <a
              key={l.label}
              href={l.href}
              target={l.href.startsWith('http') ? '_blank' : undefined}
              rel={l.href.startsWith('http') ? 'noopener noreferrer' : undefined}
              className="text-muted-foreground/60 text-xs hover:text-foreground transition-colors"
            >
              {l.label}
            </a>
          ))}
        </div>
        <img src="/badges/sportradar-powered-by.png" alt="Powered by Sportradar" className="h-4 opacity-60 mx-auto" />
        {/* SAB-722: was "21+ in select states ... call 1-800-522-4700", which was wrong
            twice over — no state we operate in requires 21 (18 everywhere except
            Nebraska at 19, and all four pending states are 18), and the helpline did
            not match the one used everywhere else. This footer renders on every legal
            and info page including /withdrawals, the page built for Soap's card-brand
            review. Wording now matches MarketingFooter exactly. */}
        <p className="text-muted-foreground/40 text-[10px] max-w-md mx-auto">
          Must be 18+ (19+ in Nebraska). Responsible gaming: call or text{' '}
          <b className="text-foreground">1-800-GAMBLER</b> or visit{' '}
          <a
            href="https://www.ncpgambling.org"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-foreground"
          >
            ncpgambling.org
          </a>.
        </p>
        <p className="text-muted-foreground/30 text-xs">
          &copy; {new Date().getFullYear()} Sabotage Fantasy LLC. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
